import React from "react";
import Tooltip from "@mui/material/Tooltip";

// Shared display pieces for metric cards and metric table rows: value
// formatting by source format, the single-flag status chip, and the
// sparkline. Status vocabulary comes from the backend threshold evaluator:
// on_target | flagged | no_data | no_target.
//
// The sparkline mirrors the schedule view's WeeklySummaryCards pattern:
// fixed 100x28 viewBox with non-scaling strokes (so nothing distorts when
// the card stretches), a soft area fill, and a per-day hover with crosshair
// plus a light tooltip showing the date and formatted value.

const lightTooltipSlotProps = {
  tooltip: {
    sx: {
      bgcolor: "white",
      color: "#1e293b",
      border: "1px solid #e2e8f0",
      borderRadius: "10px",
      boxShadow: "0 12px 28px rgba(15, 23, 42, 0.12)",
      p: 0,
      maxWidth: "none",
    },
  },
  arrow: {
    sx: {
      color: "white",
      "&::before": {
        border: "1px solid #e2e8f0",
        backgroundColor: "white",
        boxSizing: "border-box",
      },
    },
  },
};

// WeatherAPI condition text -> emoji, for surfaces that render weather as a
// plain string (metric tables). Order matters: "Patchy rain nearby" must hit
// rain, "Partly cloudy" must hit partly before cloud, and thunder/snow win
// over the rain keyword they often contain.
const WEATHER_EMOJI = [
  [/thunder|storm/, "\u26C8\uFE0F"],
  [/snow|blizzard|sleet|ice|pellet/, "\u2744\uFE0F"],
  [/rain|drizzle|shower/, "\uD83C\uDF27\uFE0F"],
  [/fog|mist|haze/, "\uD83C\uDF2B\uFE0F"],
  [/partly/, "\u26C5"],
  [/cloud|overcast/, "\u2601\uFE0F"],
  [/sun|clear/, "\u2600\uFE0F"],
];

export function weatherEmoji(text) {
  const lowered = String(text || "").toLowerCase();
  for (const [pattern, emoji] of WEATHER_EMOJI) {
    if (pattern.test(lowered)) {
      return emoji;
    }
  }
  return "";
}

export function formatMetricValue(value, format) {
  if (value === null || value === undefined) {
    return "--";
  }
  // Weather values are the serializer dict, not a number: without this the
  // numeric fallback below renders NaN anywhere a weather source is not a
  // card (metric tables). Mirrors email_payload.format_metric_value; the
  // card keeps its richer icon treatment.
  if (format === "weather") {
    if (!value || typeof value !== "object") {
      return String(value);
    }
    const temp =
      value.maxtemp === null || value.maxtemp === undefined
        ? "--"
        : `${Math.round(Number(value.maxtemp))}°F`;
    return `${weatherEmoji(value.text)} ${temp} ${value.text || ""}`.trim();
  }
  if (format === "dollar") {
    return Number(value).toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: value >= 1000 ? 0 : 2,
    });
  }
  if (format === "percent") {
    return `${Number(value).toFixed(1)}%`;
  }
  if (format === "hours") {
    return Number(value).toFixed(1);
  }
  if (format === "stars") {
    return `${Number(value).toFixed(1)}★`;
  }
  return Number(value).toLocaleString("en-US", {
    maximumFractionDigits: 2,
  });
}

// Comparison columns for a metric whose own unit is a percentage are
// expressed in percentage POINTS, not percent. "vs target" was always a
// plain difference (value - target), so for a percent metric it was already
// points and only ever wore the wrong suffix; "vs last week" additionally
// switches from a relative change to a difference server-side, so the two
// columns agree. Other formats keep percent for week-over-week and the
// metric's own unit for the target gap.
export function formatVsLastWeek(value, format) {
  if (value === null || value === undefined) {
    return "--";
  }
  const sign = value > 0 ? "+" : "";
  if (format === "percent") {
    return `${sign}${Number(value).toFixed(1)} pts`;
  }
  return `${sign}${value}%`;
}

export function formatVsTarget(value, target, format) {
  if (
    value === null ||
    value === undefined ||
    target === null ||
    target === undefined ||
    typeof value === "object"
  ) {
    return "--";
  }
  const diff = Number(value) - Number(target);
  const sign = diff > 0 ? "+" : "";
  if (format === "percent") {
    return `${sign}${diff.toFixed(1)} pts`;
  }
  return `${sign}${formatMetricValue(diff, format)}`;
}

export function formatTargetLine(threshold, direction) {
  if (!threshold || threshold.target === null || threshold.target === undefined) {
    return null;
  }
  const targetText = Number(threshold.target).toLocaleString("en-US", {
    maximumFractionDigits: 2,
  });
  return direction === "flag_above"
    ? `Flag above ${targetText}`
    : `Flag below ${targetText}`;
}

export function StatusChip(props) {
  const status = props.status;
  // The metric TABLE packs these into a column it shares with three others,
  // where the card's 11px chip is both the widest thing in the row and the
  // same size as the body text. compact only shrinks the chrome - the cards
  // keep the original sizing.
  const size = props.compact
    ? "text-[10px] px-1.5 py-0"
    : "text-[11px] px-2 py-0.5";
  if (status === "flagged") {
    return (
      <span className={`inline-flex items-center gap-1 font-bold text-rose-700 bg-rose-50 rounded-full whitespace-nowrap ${size}`}>
        ⚑ Flagged
      </span>
    );
  }
  if (status === "on_target") {
    return (
      <span className={`inline-flex items-center gap-1 font-bold text-emerald-700 bg-emerald-50 rounded-full whitespace-nowrap ${size}`}>
        ▲ On target
      </span>
    );
  }
  if (status === "no_data") {
    return (
      <span className={`inline-flex items-center gap-1 font-bold text-slate-500 bg-slate-100 rounded-full whitespace-nowrap ${size}`}>
        No data yet
      </span>
    );
  }
  if (props.showNoTarget) {
    return (
      <span className="inline-block font-medium text-slate-300" aria-label="No target">
        —
      </span>
    );
  }
  return null;
}

const formatSparklineDate = (dateStr) => {
  const parsed = new Date(`${dateStr}T00:00:00`);
  return parsed.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
};

export function Sparkline(props) {
  const [hoveredIndex, setHoveredIndex] = React.useState(null);
  const series = props.series || [];
  const color = "#6d28d9";
  const width = 100;
  const height = 28;

  const points = series
    .map((point, i) => ({ i, date: point.date, value: point.value }))
    .filter((point) => point.value !== null && point.value !== undefined);
  if (points.length < 2) {
    return null;
  }

  const values = points.map((point) => point.value);
  const rawMax = Math.max(...values);
  const rawMin = Math.min(...values);
  const span = rawMax - rawMin || 1;
  const padded = span * 0.15;
  const max = rawMax + padded;
  // Clamp the floor to zero only for non-negative series (keeps the nice
  // zero baseline); series that dip negative (e.g. sales minus labor cost)
  // keep their padded minimum so no point renders outside the chart.
  const min = rawMin >= 0 ? Math.max(0, rawMin - padded) : rawMin - padded;
  const range = max - min || 1;
  const stepX = width / (series.length - 1 || 1);

  const coords = points.map((point) => ({
    ...point,
    x: point.i * stepX,
    y: height - 1 - ((point.value - min) / range) * (height - 2),
  }));
  const linePoints = coords.map((c) => `${c.x},${c.y}`).join(" ");
  const areaPoints = `${coords[0].x},${height} ${linePoints} ${
    coords[coords.length - 1].x
  },${height}`;
  const last = coords[coords.length - 1];

  // A value on TODAY is a partial day (data still accruing), so the final
  // segment renders dashed and lighter, matching the schedule summary cards'
  // in-progress treatment. Series with no today value simply end solid at
  // the last complete day.
  const now = new Date();
  const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(
    now.getDate()
  ).padStart(2, "0")}`;
  const lastIsPartialToday = coords.length >= 2 && last.date === todayStr;
  const solidCoords = lastIsPartialToday ? coords.slice(0, -1) : coords;
  const solidPoints = solidCoords.map((c) => `${c.x},${c.y}`).join(" ");
  const partialSegment = lastIsPartialToday
    ? [coords[coords.length - 2], last]
    : null;
  const hoveredCoord = hoveredIndex != null ? coords[hoveredIndex] : null;

  return (
    <div className="relative w-full h-7 mt-2">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        className="w-full h-full block"
      >
        <polygon points={areaPoints} fill={color} opacity={0.12} />
        <polyline
          points={solidPoints}
          fill="none"
          stroke={color}
          strokeWidth={1.5}
          vectorEffect="non-scaling-stroke"
        />
        {partialSegment && (
          <polyline
            points={partialSegment.map((c) => `${c.x},${c.y}`).join(" ")}
            fill="none"
            stroke={color}
            strokeWidth={1.5}
            strokeDasharray="3 2"
            opacity={0.5}
            vectorEffect="non-scaling-stroke"
          />
        )}
        {hoveredCoord && (
          <line
            x1={hoveredCoord.x}
            y1={0}
            x2={hoveredCoord.x}
            y2={height}
            stroke="#94a3b8"
            strokeWidth={0.5}
            vectorEffect="non-scaling-stroke"
            opacity={0.6}
          />
        )}
      </svg>
      {/* The endpoint and hover dots are HTML so they stay round while the
          SVG stretches. */}
      <div
        className="absolute rounded-full pointer-events-none"
        style={{
          left: `${last.x}%`,
          top: `${last.y}px`,
          width: 5,
          height: 5,
          backgroundColor: color,
          transform: "translate(-50%, -50%)",
        }}
      />
      {hoveredCoord && hoveredCoord !== last && (
        <div
          className="absolute rounded-full pointer-events-none"
          style={{
            left: `${hoveredCoord.x}%`,
            top: `${hoveredCoord.y}px`,
            width: 4,
            height: 4,
            backgroundColor: color,
            transform: "translate(-50%, -50%)",
          }}
        />
      )}
      <div
        className="absolute inset-0"
        onMouseLeave={() => setHoveredIndex(null)}
      >
        {coords.map((coord, i) => {
          const pointX = coord.x;
          const prevX = i > 0 ? coords[i - 1].x : null;
          const nextX = i < coords.length - 1 ? coords[i + 1].x : null;
          const slotLeft = prevX != null ? (prevX + pointX) / 2 : 0;
          const slotRight = nextX != null ? (pointX + nextX) / 2 : width;
          return (
            <Tooltip
              key={i}
              arrow
              placement="top"
              disableInteractive
              enterDelay={150}
              enterNextDelay={75}
              slotProps={lightTooltipSlotProps}
              title={
                <div className="px-2.5 py-1.5">
                  <p className="text-[11px] font-semibold text-slate-500">
                    {formatSparklineDate(coord.date)}
                  </p>
                  <p className="text-sm font-bold text-slate-900 tabular-nums">
                    {formatMetricValue(coord.value, props.format)}
                  </p>
                </div>
              }
            >
              <div
                className="absolute top-0 h-full cursor-default"
                style={{
                  left: `${slotLeft}%`,
                  width: `${slotRight - slotLeft}%`,
                }}
                onMouseEnter={() => setHoveredIndex(i)}
              />
            </Tooltip>
          );
        })}
      </div>
    </div>
  );
}
